import React, { useState, useEffect } from 'react';
import { supabase, supabase_get_vacancy_applications, supabase_submit_application, supabase_get_candidates_light, supabase_get_paginated_talent_pool } from '../services/supabaseService';
import { performRealMatch, matchTalentPool } from '../services/geminiService';
import { Mail, User, FileText, CheckCircle2, XCircle, Sparkles, Sliders, ChevronDown, ArrowLeft, BrainCircuit, Search, Plus, Users, CheckSquare, Square, MessageCircle, Phone, Star, X } from 'lucide-react';
import { Vacancy, UserRole } from '../types';
import CandidateDetailModal from './CandidateDetailModal';
import BulkEmailComposerModal from './BulkEmailComposerModal';
import { DebugTag } from './DebugTag';

interface VacancyCandidatesProps {
  vacancyId: string;
  userRole?: UserRole;
  clientId?: string; // Passed from dashboard if available
}

import { filterCitiesByRadius, getCityDistancesKms } from '../services/googleMapsService';

// --- BACKGROUND SCREENING PERSISTENCE ---
// By keeping the tracking state in a module-scoped variable, we ensure that navigating 
// to a different tab does not 'kill' the state. The async handleScreenPool loop naturally continues.
type ScreeningJob = { isScreening: boolean, progress: { current: number, total: number, tokensUsed: number } };
const screeningJobs: Record<string, ScreeningJob> = {};
const screeningEventTarget = new EventTarget();

const updateScreeningJob = (vacancyId: string, updates: Partial<ScreeningJob>) => {
    if (!screeningJobs[vacancyId]) screeningJobs[vacancyId] = { isScreening: false, progress: { current: 0, total: 0, tokensUsed: 0 } };
    screeningJobs[vacancyId] = { ...screeningJobs[vacancyId], ...updates };
    screeningEventTarget.dispatchEvent(new CustomEvent('screening_update', { detail: vacancyId }));
};

const VacancyCandidates: React.FC<VacancyCandidatesProps> = ({ vacancyId, userRole, clientId }) => {
  const [applications, setApplications] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Screening State
  const [screenLimit, setScreenLimit] = useState(50);
  const [minMatchScore, setMinMatchScore] = useState(0);

  // Read initial background state if it exists
  const [isScreening, setIsScreeningState] = useState(() => screeningJobs[vacancyId]?.isScreening || false);
  const [screeningProgress, setScreeningProgressState] = useState(() => screeningJobs[vacancyId]?.progress || { current: 0, total: 0, tokensUsed: 0 });

  // Sync state functions that update BOTH local component and global tracker
  const setIsScreening = (val: boolean) => { setIsScreeningState(val); updateScreeningJob(vacancyId, { isScreening: val }); };
  const setScreeningProgress = (progress: any) => { setScreeningProgressState(progress); updateScreeningJob(vacancyId, { progress }); };

  useEffect(() => {
     const onUpdate = (e: any) => {
         if (e.detail === vacancyId && screeningJobs[vacancyId]) {
             setIsScreeningState(screeningJobs[vacancyId].isScreening);
             setScreeningProgressState(screeningJobs[vacancyId].progress);
         }
     };
     screeningEventTarget.addEventListener('screening_update', onUpdate);
     return () => screeningEventTarget.removeEventListener('screening_update', onUpdate);
  }, [vacancyId]);
  const [vectorSearchCount, setVectorSearchCount] = useState<number | null>(null);
  const [matches, setMatches] = useState<any[]>([]);
  const [vectorCandidates, setVectorCandidates] = useState<any[]>([]); // To run pagination without re-querying DB

  const [allScreeningResults, setAllScreeningResults] = useState<any[]>([]); // All results for "Toon meer" 
  const [screeningComplete, setScreeningComplete] = useState(false);
  const [interactivePhase, setInteractivePhase] = useState<number>(0);
  const [displayProgress, setDisplayProgress] = useState(0);

  useEffect(() => {
    if (isScreening && interactivePhase >= 5) {
      const actualCount = screeningProgress?.current || 0;
      const totalCount = screeningProgress?.total || screenLimit;
      const maxAllowed = Math.min(totalCount, actualCount + 15);

      if (displayProgress < maxAllowed) {
        const timer = setTimeout(() => {
          setDisplayProgress(prev => prev + 1);
        }, 200);
        return () => clearTimeout(timer);
      } else if (displayProgress < actualCount) {
         setDisplayProgress(actualCount);
      }
    } else if (!isScreening && displayProgress > 0) {
      setDisplayProgress(0);
    }
  }, [displayProgress, isScreening, interactivePhase, screeningProgress, screenLimit]);

  const [selectedMatches, setSelectedMatches] = useState<string[]>([]);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [confirmInviteMatch, setConfirmInviteMatch] = useState<any>(null);
  const [isInviting, setIsInviting] = useState(false);
  const [matchKeywordFilter, setMatchKeywordFilter] = useState('');
  const [matchLocationFilter, setMatchLocationFilter] = useState('');
  const [matchDistanceFilter, setMatchDistanceFilter] = useState('');
  const [matchStatusFilter, setMatchStatusFilter] = useState('');
  const [matchAvailabilityFilter, setMatchAvailabilityFilter] = useState('');
  const [matchExtraRequest, setMatchExtraRequest] = useState('');
  const [matchSortOrder, setMatchSortOrder] = useState<'score' | 'distance'>('score');
  const [displayCount, setDisplayCount] = useState(25);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const getCityFromAny = (obj: any): string => {
      if (!obj) return '';
      const c1 = obj.candidate?.candidate || obj.candidate || obj;
      return c1.address_city || c1.city || c1.location || 
             obj.candidate?.address_city || obj.candidate?.city || obj.candidate?.location || 
             obj.address_city || obj.city || obj.location || 
             obj.ai_parsed_data?.city || obj.parsed_cv?.city || obj.ai_parsed_data?.address_city || obj.parsed_cv?.address_city ||
             obj.candidate?.ai_parsed_data?.city || obj.candidate?.parsed_cv?.city || obj.candidate?.ai_parsed_data?.location || '';
  };

  // AI pre-screening criteria
  const [manualAiContext, setManualAiContext] = useState('');
  const [requiredSkills, setRequiredSkills] = useState<string[]>([]);
  const [newSkillInput, setNewSkillInput] = useState('');
  // Database Pre-Match Filters
  const [preFilterLocation, setPreFilterLocation] = useState('');
  const [preFilterRadiusKm, setPreFilterRadiusKm] = useState<number | ''>(50);
  const [preFilterKeyword, setPreFilterKeyword] = useState('');
  const [preFilterStatus, setPreFilterStatus] = useState('');
  const [isPreFiltering, setIsPreFiltering] = useState(false);
  const [poolCandidates, setPoolCandidates] = useState<any[]>([]);
  const [filteredPoolCandidates, setFilteredPoolCandidates] = useState<any[]>([]);

  useEffect(() => {
    // Synchronize initial base state
    setFilteredPoolCandidates(poolCandidates);
  }, [poolCandidates]);

  const applyPreFilters = async () => {
      setIsPreFiltering(true);
      try {
          let allowedCities: string[] | null = null;
          if (matchLocationFilter && matchDistanceFilter && Number(matchDistanceFilter) > 0) {
              const uniqueCities = Array.from(new Set(poolCandidates.map(c => getCityFromAny(c)).filter(Boolean)));
              allowedCities = await filterCitiesByRadius(matchLocationFilter, uniqueCities as string[], Number(matchDistanceFilter));
          } else if (matchLocationFilter) {
              allowedCities = [matchLocationFilter.toLowerCase()];
          }

          const subset = poolCandidates.filter(c => {
              if (matchStatusFilter && c.status !== matchStatusFilter && c.candidate?.status !== matchStatusFilter) return false;
              let city = getCityFromAny(c);
              if (allowedCities && !allowedCities.includes(city.toLowerCase()) && !city.toLowerCase().includes(matchLocationFilter.toLowerCase())) {
                  return false;
              }
              if (preFilterKeyword) {
                  const kw = preFilterKeyword.toLowerCase();
                  let searchTerms = [
                     c.candidate?.name, c.candidate?.role || c.ai_parsed_data?.currentRole || c.ai_parsed_data?.role || c.parsed_cv?.currentRole || c.parsed_cv?.role, c.parsed_cv?.role,
                     ...(c.parsed_cv?.skills || []), c.parsed_cv?.summary
                  ].filter(Boolean).join(' ').toLowerCase();
                  if (!searchTerms.includes(kw)) return false;
              }
              return true;
          });
          setFilteredPoolCandidates(subset);
      } catch (e) { console.error(e); }
      setIsPreFiltering(false);
  };

  const [searchInCurrentMatches, setSearchInCurrentMatches] = useState(false);
  const [vacancyData, setVacancyData] = useState<any>(null);
  const [cityDistances, setCityDistances] = useState<Record<string, number>>({});
  const [selectedCandidate, setSelectedCandidate] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [browseLocationFilter, setBrowseLocationFilter] = useState('');
  const [browseStatusFilter, setBrowseStatusFilter] = useState('');
  const [browseResults, setBrowseResults] = useState<any[]>([]);
  const [browseTotal, setBrowseTotal] = useState(0);
  const [browsePage, setBrowsePage] = useState(1);
  const [isBrowseLoading, setIsBrowseLoading] = useState(false);

  // Function to save minimal match output to the Vacancy JSONB column to prevent DB bloat
  const saveMatchesToDatabase = async (matchesArray: any[], vData: any) => {
      if (!vacancyId || !matchesArray.length) return;
      const minimalMatches = matchesArray.map(m => ({
           candidateId: m.candidate?.id || m.candidate?.candidate?.id || m.candidateId,
           matchScore: m.matchScore,
           reasoning: m.reasoning,
           pros: m.pros,
           cons: m.cons,
           // Cache preview data for instant loading
           _preview: {
               name: m.candidate?.candidate?.name || m.candidate?.name,
               role: m.candidate?.candidate?.role || m.candidate?.ai_parsed_data?.currentRole || m.candidate?.parsed_cv?.currentRole || m.candidate?.parsed_cv?.role || m.candidate?.role,
               city: m.candidate?.candidate?.address_city || m.candidate?.address_city || m.candidate?.candidate?.city,
               status: m.candidate?.status || m.candidate?.candidate?.status
           },
           isManualAdd: m.isManualAdd
      }));
      
      const updatedData = {
           ...(vData?.data || {}),
           ai_matches: minimalMatches
      };

      await supabase.from('vacancies').update({ data: updatedData }).eq('id', vacancyId);
  };

  const [allowedMatchCities, setAllowedMatchCities] = useState<string[] | null>(null);

  useEffect(() => {
     const calcCities = async () => {
         if (matchLocationFilter && matchDistanceFilter && Number(matchDistanceFilter) > 0) {
             const uniqueCities = Array.from(new Set(matches.map(m => getCityFromAny(m)).filter(Boolean)));
             const allowed = await filterCitiesByRadius(matchLocationFilter, uniqueCities as string[], Number(matchDistanceFilter));
             setAllowedMatchCities(allowed);
         } else if (matchLocationFilter) {
             setAllowedMatchCities([matchLocationFilter.toLowerCase()]);
         } else {
             setAllowedMatchCities(null);
         }
     };
     calcCities();
  }, [matchLocationFilter, matchDistanceFilter, matches]);

  const filteredMatches = matches.filter(m => {
      if (m.isManualAdd) return true;
      if (matchStatusFilter && m.candidate?.status !== matchStatusFilter) return false;
      
      const city = getCityFromAny(m);
      
      if (allowedMatchCities) {
          if (!allowedMatchCities.includes(city.toLowerCase()) && !city.toLowerCase().includes(matchLocationFilter.toLowerCase())) {
              return false;
          }
      }
      
      if (matchKeywordFilter) {
          let searchTerms = [
             m.candidate?.candidate?.name,
             m.candidate?.candidate?.role || m.candidate?.ai_parsed_data?.currentRole || m.candidate?.ai_parsed_data?.role || m.candidate?.parsed_cv?.currentRole || m.candidate?.parsed_cv?.role,
             m.candidate?.parsed_cv?.role,
             ...(m.candidate?.parsed_cv?.skills || []),
             ...(m.candidate?.parsed_cv?.tags || []),
             m.candidate?.parsed_cv?.summary
          ].filter(Boolean).join(' ').toLowerCase();
          
          const kw = matchKeywordFilter.toLowerCase();
          if (!searchTerms.includes(kw)) return false;
      }
      
      return true;
  });

  const sortedMatches = [...filteredMatches].sort((a, b) => {
      if (matchSortOrder === 'score') {
          return b.matchScore - a.matchScore;
      } else {
          const cityA = getCityFromAny(a);
          const cityB = getCityFromAny(b);
          // Missing distance defaults to high number to appear at the bottom
          const distA = cityDistances[cityA.toLowerCase()] !== undefined && cityDistances[cityA.toLowerCase()] >= 0 ? cityDistances[cityA.toLowerCase()] : 99999;
          const distB = cityDistances[cityB.toLowerCase()] !== undefined && cityDistances[cityB.toLowerCase()] >= 0 ? cityDistances[cityB.toLowerCase()] : 99999;
          
          if (distA === distB) {
              return b.matchScore - a.matchScore;
          }
          return distA - distB;
      }
  });

  const matchUniqueLocations = Array.from(new Set(matches.map(m => getCityFromAny(m)).filter(Boolean))).sort();
  const matchUniqueSkills = Array.from(new Set(matches.flatMap(m => m.candidate?.parsed_cv?.skills || []))).filter(Boolean).sort();

  const handleSelectAll = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    // If we have selected all currently matching, deselect all. Otherwise, select all filtered matches.
    if (selectedMatches.length > 0 && selectedMatches.length === filteredMatches.length) {
      setSelectedMatches([]);
    } else {
      setSelectedMatches(filteredMatches.map(m => m.candidate?.id || m.candidate?.candidate?.id));
    }
  };

  const handleSelectMatch = (id: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    if (selectedMatches.includes(id)) {
      setSelectedMatches(selectedMatches.filter(sid => sid !== id));
    } else {
      setSelectedMatches([...selectedMatches, id]);
    }
  };

  const handleBulkEmail = () => {
     setShowEmailModal(true);
  };

  const handleBulkWhatsapp = async () => {
    const selected = matches.filter(m => {
        const id = m.candidate?.id || m.candidate?.candidate?.id;
        return selectedMatches.includes(id);
    });
    const validPhones = selected.filter(m => m.candidate?.candidate?.phone);
    if (validPhones.length === 0) {
      alert("Geen telefoonnummers gevonden voor de geselecteerde kandidaten.");
      return;
    }
    
    setIsScreening(true); // Reuse loader to lock UI
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id || 'unknown';
      let count = 0;
      
      for (const match of validPhones) {
        const profileId = match.candidate?.id;
        if (!profileId) continue;
        
        const firstName = (match.candidate?.candidate?.name || 'kandidaat').split(' ')[0];
        const text = `Hoi ${firstName}, we hebben een interessante vacature ("${vacancyData?.title || 'Vacature'}") die mogelijk goed aansluit bij jouw profiel!`;
        
        await supabase.from('candidate_communications').insert({
            profile_id: profileId,
            user_id: userId,
            type: 'whatsapp_draft',
            content: `${text}\n\n---RAW_TRANSCRIPT---`
        });
        count++;
      }
      alert(`${count} WhatsApp concept(en) klaargezet in het Postvak Uit op het profiel van de kandidaten!`);
      setSelectedMatches([]);
    } catch (e) {
      console.error(e);
      alert("Fout bij aanmaken WhatsApp concepten.");
    } finally {
      setIsScreening(false);
    }
  };
  
  useEffect(() => {
     const calculateDistances = async () => {
         const origin = vacancyData?.data?.location || vacancyData?.location || vacancyData?.address_city;
         if (!origin || !matches.length) return;
         
         const uniqueCities = Array.from(new Set(matches.map(m => getCityFromAny(m)).filter(Boolean)));
         const missingCities = uniqueCities.filter(c => cityDistances[c.toLowerCase()] === undefined);
         
         if (missingCities.length > 0) {
             // To prevent continuous retries for cities that Google cannot resolve, mark them with -1 first
             // The getCityDistancesKms will overwrite them with correct distances if successful
             const initialMap: Record<string, number> = {};
             missingCities.forEach(c => initialMap[c.toLowerCase()] = -1);
             setCityDistances(prev => ({...prev, ...initialMap}));
             
             const newDistances = await getCityDistancesKms(origin, missingCities);
             setCityDistances(prev => ({...prev, ...newDistances}));
         }
     };
     calculateDistances();
  }, [matches, vacancyData]);

  // Full Screen View State

  // Browse state - server-side paginated
  const BROWSE_PAGE_SIZE = 24;

  const runBrowseSearch = async (query: string, page: number = 1) => {
    setIsBrowseLoading(true);
    try {
      const currentFilterId = (userRole === UserRole.CLIENT || userRole === UserRole.CLIENT_RECRUITER)
        ? (clientId)
        : undefined;
      const result = await supabase_get_paginated_talent_pool(
        currentFilterId, page, BROWSE_PAGE_SIZE, query, 'created_at', false, false
      );
      setBrowseResults(result?.data || []);
      setBrowseTotal(result?.count || 0);
      setBrowsePage(page);
    } catch (e) {
      console.error('Browse search error:', e);
    } finally {
      setIsBrowseLoading(false);
    }
  };

  useEffect(() => {
    if (vacancyId) {
      loadData();
      runBrowseSearch(''); // initial browse load
    }
  }, [vacancyId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Load vacancy details
      const { data: vData } = await supabase.from('vacancies').select('title, description, data, client_id').eq('id', vacancyId).single();
      setVacancyData(vData);
      
      // Auto-prefill the target location for distance search based on the vacancy location
      // REMOVED based on user request (no standard fill-in of location filter)
      // if (vData?.data?.location) {
      //     setMatchLocationFilter(vData.data.location);
      //     setMatchDistanceFilter('25');
      // }

      // Instantly load cached matches to bypass long loading ONLY IF it is not currently screening
      if (vData?.data?.ai_matches && Array.isArray(vData.data.ai_matches)) {
          // If a background screening is running, DO NOT overwrite the empty 'matches' loading state
          if (!screeningJobs[vacancyId]?.isScreening) {
              const cached = vData.data.ai_matches.map((m: any) => ({
              candidateId: m.candidateId,
              matchScore: m.matchScore,
              reasoning: m.reasoning,
              pros: m.pros || [],
              cons: m.cons || [],
              candidate: {
                  id: m.candidateId,
                  candidate: {
                      id: m.candidateId,
                      name: m._preview?.name || 'Kandidaat',
                      role: m._preview?.role || '',
                      address_city: m._preview?.city || '',
                      city: m._preview?.city || '',
                      status: m._preview?.status || ''
                  }
              },
              isManualAdd: m.isManualAdd
          }));
          setMatches(cached);
          setScreeningComplete(true);
          setIsLoading(false); // <--- Instantly unblock UI!
        }
      }

      // Load applications to filter out people who already applied/are invited
      const apps = await supabase_get_vacancy_applications(vacancyId);
      setApplications(apps || []);

      // (End of reconstruction block, removed to prevent ReferenceError)

    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false); // Ensure loader stops even if first cache didn't hit
    }
  };

  const handleScreenPool = async () => {
    // Prevent starting a new one if it's already doing it globally
    if (!vacancyData || screeningJobs[vacancyId]?.isScreening) return;
    setIsScreening(true);
    setInteractivePhase(1);
    const previousMatches = matches;
    setMatches([]); // Clear previous results only on first search
    setScreeningComplete(false);
    try {
      // Pool candidates check removed since we lazy load from PGVector.

      // By default validCandidates is empty since we don't eager load anymore. Let pgvector handle it.
      const validCandidates = filteredPoolCandidates.length > 0 ? filteredPoolCandidates : [];

      const { screenTalentPoolBatch, generateEmbedding } = await import('../services/geminiService');

      const extraContext = [
          requiredSkills.length > 0 ? `Vereiste Skills: ${requiredSkills.join(', ')}` : '',
          manualAiContext ? `Extra Aandachtspunten: ${manualAiContext}` : ''
      ].filter(Boolean).join('\n');

      let preciseCandidatesForAI: any[] = [];

      if (searchInCurrentMatches && previousMatches.length > 0) {
          // Bypass semantic search: only use the candidates already present in the previous matches list
          preciseCandidatesForAI = previousMatches.map(m => m.candidate);
          setVectorCandidates(preciseCandidatesForAI);
      } else {
          // 1. Semantic Vector Match: Genereer de Vacature embedding (1x)
          const coreDesc = vacancyData.data?.finalVacancyContent || vacancyData.description || '';
          const vacancyString = `Titel: ${vacancyData.title}\nCategorie: ${vacancyData.data?.category || ''}\nBeschrijving: ${coreDesc.substring(0, 1000)}\n\n${extraContext}`;
          const queryEmbedding = await generateEmbedding(vacancyString);

          if (!queryEmbedding) {
              // Embedding API unavailable (e.g. geographic restriction) â€” fall back to direct AI screening
              console.warn("[VacancyCandidates] Embedding API unavailable. Falling back to direct AI screening without vector pre-filter.");
              
              let fallbackPool = validCandidates;
              if (fallbackPool.length === 0) {
                 const currentFilterId = (userRole === UserRole.CLIENT || userRole === UserRole.CLIENT_RECRUITER) ? (clientId || vacancyData?.client_id) : undefined;
                 const { supabase_get_candidates_light } = await import('../services/supabaseService');
                 const p = await supabase_get_candidates_light(currentFilterId);
                 fallbackPool = p || [];
              }
              
              if (fallbackPool.length === 0) {
                 alert("Geen kandidaten in database om te screenen.");
                 setIsScreening(false);
                 updateScreeningJob(vacancyId, { isScreening: false });
                 return;
              }
              
              const fallbackLimit = Math.min(fallbackPool.length, screenLimit);
              
              // We must fetch full profiles for the fallback limit
              const hitIds = fallbackPool.slice(0, fallbackLimit).map((c: any) => c.candidate_id || c.id || c.candidate?.id);
              const { data: hitProfiles } = await supabase
                .from('candidates')
                .select('*')
                .in('id', hitIds);
                
              const profilesByCandidateId = new Map();
              for (const p of (hitProfiles || [])) {
                p.candidate = p; // Ensure backward compatibility where component expects p.candidate.name
                p.candidate_id = p.id;
                profilesByCandidateId.set(p.id, p);
              }
              preciseCandidatesForAI = Array.from(profilesByCandidateId.values()).map(p => ({
                ...p,
                id: p.id,
                parsed_cv: p.ai_parsed_data || {},
              }));
              setVectorCandidates(preciseCandidatesForAI);
          } else {
              setInteractivePhase(1);
              await new Promise(r => setTimeout(r, 800));

              // Krijg een live indicatie van hoeveel vector-embedded CV's er momenteel worden doorzocht
              const { count: vCount } = await supabase.from('candidates').select('*', { count: 'exact', head: true }).not('embedding', 'is', 'null');
              setVectorSearchCount(vCount || 0);

              setInteractivePhase(2);
              await new Promise(r => setTimeout(r, 800));

              // 2. Supabase Similarity Search (pgvector HNSW) -> Ophalen Top 1000 kandidaten
              const { data: vectorHits, error: vectorError } = await supabase.rpc('match_candidates', {
                query_embedding: queryEmbedding,
                match_threshold: 0.1, // Extreem lage threshold zodat we altijd de top teruggeven
                match_count: 10000, // Extreem hoge limiet zodat letterlijk alle mogelijke kandidaten door de filter gaan
                p_client_id: (userRole === UserRole.CLIENT || userRole === UserRole.CLIENT_RECRUITER) ? (clientId || vacancyData?.client_id) : null
              });

              if (vectorError) {
                  console.error("Vector Search RPC Error:", vectorError);
                  alert("Fout tijdens ophalen Vector database. Staat pgvector aan op Supabase en heb je het sql script gedraaid?");
                  setIsScreening(false);
                  updateScreeningJob(vacancyId, { isScreening: false });
                  return;
              }

              if (!vectorHits || vectorHits.length === 0) {
                  alert("De filter leverde 0 bruikbare resultaten op uit de database op basis van de huidige regio. Waarschijnlijke oorzaak: De regio-eisen zijn te streng, er zijn écht geen resultaten, of we wachten nog op data-synchronisatie.");
                  setIsScreening(false);
                  updateScreeningJob(vacancyId, { isScreening: false });
                  return;
              }
              
              let validHitIds = vectorHits.map((h: any) => h.candidate_id);

              // 2.5 Apply UI Pre-Filters (Location, Status, Keyword) to the broad Vector Hits
              if (matchLocationFilter || matchStatusFilter || (preFilterKeyword && preFilterKeyword.trim() !== '')) {
                  // Chunk the hit IDs because getting UUIDs makes the URL too long (400 Bad Request)
                  // 40 UUIDs = ~1500 chars, safely under the 2048-char URI limit of PostgREST
                  const chunkSize = 40;
                  const chunkMetaPromises = [];
                  for (let i = 0; i < validHitIds.length; i += chunkSize) {
                      const chunk = validHitIds.slice(i, i + chunkSize);
                      chunkMetaPromises.push(
                          supabase.from('candidates').select('id, address_city, city, location, status, role, skill_set, cv_summary, name, ai_parsed_data, parsed_cv').in('id', chunk)
                      );
                  }
                  
                  const chunkResults = await Promise.all(chunkMetaPromises);
                  const filterMeta = chunkResults.flatMap(r => r.data || []);
                  
                  if (filterMeta && filterMeta.length > 0) {
                      let allowedCities: string[] | null = null;
                      if (matchLocationFilter) {
                           if (matchDistanceFilter && Number(matchDistanceFilter) > 0) {
                               const uniqueCities = Array.from(new Set(filterMeta.map(c => getCityFromAny(c)).filter(Boolean)));
                               const { filterCitiesByRadius } = await import('../services/googleMapsService');
                               allowedCities = await filterCitiesByRadius(matchLocationFilter, uniqueCities as string[], Number(matchDistanceFilter));
                           } else {
                               allowedCities = [matchLocationFilter.toLowerCase()];
                           }
                      }
                      
                      const kw = preFilterKeyword ? preFilterKeyword.toLowerCase() : '';
                      
                      const validIdsSet = new Set(filterMeta.filter(c => {
                          if (matchStatusFilter && c.status !== matchStatusFilter) return false;
                          
                          if (allowedCities) {
                              const city = getCityFromAny(c);
                              if (!allowedCities.includes(city.toLowerCase()) && !city.toLowerCase().includes(matchLocationFilter.toLowerCase())) {
                                  return false;
                              }
                          }
                          
                          if (kw) {
                              const text = [c.name, c.role, c.skill_set?.join(' '), c.cv_summary].join(' ').toLowerCase();
                              if (!text.includes(kw)) return false;
                          }
                          
                          if (matchAvailabilityFilter) {
                              const avail = c.ai_parsed_data?.available_from || c.parsed_cv?.available_from;
                              if (!avail) return false;
                              const dt = new Date(avail);
                              const fDt = new Date(matchAvailabilityFilter);
                              if (dt > fDt) return false;
                          }
                          
                          return true;
                      }).map(c => c.id));
                      
                      // Keep Vector sort order intact
                      validHitIds = vectorHits.filter((h: any) => validIdsSet.has(h.candidate_id)).map((h: any) => h.candidate_id);
                  }
              }

              // Take only the Absolute Top 100 of the remaining valid hits to save API calls
              validHitIds = validHitIds.slice(0, 100);
              
              if (validHitIds.length === 0) {
                  alert("Alle vector matches zijn uitgesloten door de actieve Locatie of Status filters. Probeer een grotere afstand of verwijder het filter.");
                  setIsScreening(false);
                  updateScreeningJob(vacancyId, { isScreening: false });
                  return;
              }

              setInteractivePhase(3);
              await new Promise(r => setTimeout(r, 800));

              // 3. Fetch profiles directly from DB for the precise vector hit IDs
              const { data: hitProfiles } = await supabase
                .from('candidates')
                .select('*')
                .in('id', validHitIds)
                .limit(100);
              // Normalize: set id = candidate_id so screenTalentPoolBatch returns matching candidateIds
              // Also deduplicate: pick best profile per candidate_id
              const profilesByCandidateId = new Map();
              for (const p of (hitProfiles || [])) {
                p.candidate = p; // Backward compatibility
                p.candidate_id = p.id;
                const key = p.id;
                if (!profilesByCandidateId.has(key) || (p.ai_parsed_data && !profilesByCandidateId.get(key).ai_parsed_data)) {
                  profilesByCandidateId.set(key, p);
                }
              }
              preciseCandidatesForAI = Array.from(profilesByCandidateId.values()).map(p => ({
                ...p,
                id: p.id, // Override so AI returns id as candidateId
                parsed_cv: p.ai_parsed_data || {},
              }));
              setVectorCandidates(preciseCandidatesForAI);
              if (preciseCandidatesForAI.length === 0) {
                  alert("De ge-embedde CV's behoren niet (meer) tot kandidaten in de database.");
                  setIsScreening(false);
                  updateScreeningJob(vacancyId, { isScreening: false });
                  return;
              }
          }
      }

      // 4. Stuur deze precieze, gerankte selectie door naar Froukje AI
      setInteractivePhase(4);
      await new Promise(r => setTimeout(r, 800));

      setInteractivePhase(5);
      // Beperk Froukje's denkkracht tot enkel het aandeel dat de gebruiker heeft gevraagd (screenLimit) ter snelheid
      const candidatesToScreen = preciseCandidatesForAI.slice(0, screenLimit);

      setScreeningProgress({ current: 0, total: candidatesToScreen.length, tokensUsed: 0 });
      
      const enhancedDescription = vacancyData.description + (matchExtraRequest ? `\n\nEXTRA EISEN VAN RECRUITER (STRICT BEOORDELEN HIEROP): ${matchExtraRequest}` : '');
      
      const resultMatches = await screenTalentPoolBatch(
          { title: vacancyData.title, description: enhancedDescription },
          candidatesToScreen,
          screenLimit,
          (current, total, tokensUsed) => {
             setScreeningProgress({ current, total, tokensUsed });
          }
      );

      const findCandForMatch = (m: any) =>
        preciseCandidatesForAI.find(
          (p: any) =>
            p.candidate_id === m.candidateId || p.id === m.candidateId || p.candidate?.id === m.candidateId
        ) ||
        poolCandidates.find(
          (p: any) =>
            p.candidate_id === m.candidateId || p.id === m.candidateId || p.candidate?.id === m.candidateId
        );

      const rawAiRows = resultMatches || [];
      const unmatchedCandidateIds = rawAiRows
        .filter((m: any) => m?.candidateId != null && !findCandForMatch(m))
        .map((m: any) => String(m.candidateId));

      // Map back to expected format
      const parsedMatches = rawAiRows
        .map((m: any) => {
          const cand = findCandForMatch(m);
          return {
              candidate: cand,
              matchScore: m.score ?? 50, // Default score if not provided
              reasoning: m.explanation,
              pros: m.strengths || [],
              cons: m.risks || []
          };
      })
      .filter((match: any) => match.candidate != null) // Only filter out truly not-found candidates
      .sort((a: any, b: any) => b.matchScore - a.matchScore);

      console.log('[VacancyCandidates] Froukje screen mapping', {
        sentToAI: candidatesToScreen.length,
        aiReturnedRows: rawAiRows.length,
        mappedWithCandidate: parsedMatches.length,
        unmatchedCandidateIdsSample: unmatchedCandidateIds.slice(0, 12),
        expectedCandidateIdSample: preciseCandidatesForAI.slice(0, 4).map((p: any) => String(p.id)),
      });
      // Apply min score filter only if user explicitly set one > 0
      const scoreFiltered = minMatchScore > 0
        ? parsedMatches.filter((m: any) => m.matchScore >= minMatchScore)
        : parsedMatches;
      const finalMatches = scoreFiltered.slice(0, 50);

      setMatches(finalMatches);
      setAllScreeningResults(parsedMatches); // Store all for "load more"
      setScreeningComplete(true);
      await saveMatchesToDatabase(finalMatches, vacancyData);
      
      // Since component might have unmounted during the long run, we force an update to the tracker.
      updateScreeningJob(vacancyId, { isScreening: false });

      if (finalMatches.length === 0) {
        const hint =
          rawAiRows.length === 0
            ? 'AI shortlist was empty — see [screenTalentPoolBatch] logs and POST /api/gemini/generate in Network.'
            : parsedMatches.length === 0 && unmatchedCandidateIds.length > 0
              ? 'candidateId values from AI did not match candidate UUIDs — compare unmatchedCandidateIdsSample to expectedCandidateIdSample in the log above.'
              : minMatchScore > 0
                ? `All rows below minMatchScore (${minMatchScore}).`
                : 'No rows passed filters; inspect aiReturnedRows vs mappedWithCandidate.';
        console.warn('[VacancyCandidates] No matches after screening', {
          afterScoreFilter: finalMatches.length,
          unfilteredMapped: parsedMatches.length,
          minMatchScore,
          hint,
        });
      }

    } catch (e: any) {
      console.error("Screening error", e);
      if (e?.message === '429_TOO_MANY_REQUESTS' || e?.message?.includes('429') || e?.message?.includes('Resource exhausted') || e?.message?.includes('Too Many Requests')) {
         alert("Oeps! Froukje heeft iets te veel koffie gedronken en Google vindt dat we te snel gaan. 🤖☕\n\nIk pauzeer even en ga over 30 seconden automatisch verder!");
         setIsScreening(true); // Keep it screening visually
         setTimeout(() => {
             handleScreenPool();
         }, 30000);
         return; // Don't run the finally block's setIsScreening(false)
      } else {
         alert("Fout tijdens AI screening.");
      }
    } finally {
      setIsScreening(false);
    }
  };

  const handleLoadMoreFroukje = async () => {
    if (!vacancyData) return;
    
    // We already have 100 vector-matched candidates. Find the chunk we haven't screened yet.
    const currentIndex = matches.length; 
    const targetLimit = currentIndex + 50;
    setScreenLimit(targetLimit);
    
    const candidatesToScreen = vectorCandidates.slice(currentIndex, targetLimit);
    if (candidatesToScreen.length === 0) {
        alert("Alle kandidaten uit de Vector Match zijn geanalyseerd!");
        return;
    }
    
    setIsScreening(true);
    
    // Visually speedrun through the completed phases to show the pipeline UI
    setInteractivePhase(1); 
    await new Promise(r => setTimeout(r, 150));
    setInteractivePhase(2);
    await new Promise(r => setTimeout(r, 150));
    setInteractivePhase(3);
    await new Promise(r => setTimeout(r, 150));
    setInteractivePhase(4);
    await new Promise(r => setTimeout(r, 450));
    
    setInteractivePhase(5);
    setScreeningProgress({ current: 0, total: candidatesToScreen.length, tokensUsed: 0 });
    
    try {
        const { screenTalentPoolBatch } = await import('../services/geminiService');
        const enhancedDescription = vacancyData.description + (matchExtraRequest ? `\n\nEXTRA EISEN VAN RECRUITER (STRICT BEOORDELEN HIEROP): ${matchExtraRequest}` : '');
        const newMatches = await screenTalentPoolBatch(
            { title: vacancyData.title, description: enhancedDescription },
            candidatesToScreen,
            50,
            (current, total, tokensUsed) => {
               setScreeningProgress({ current, total, tokensUsed });
            }
        );

        const findCandLoadMore = (m: any) =>
          vectorCandidates.find(
            (p: any) =>
              p.candidate_id === m.candidateId || p.id === m.candidateId || p.candidate?.id === m.candidateId
          );
        const rawLoadMore = newMatches || [];
        const unmatchedLoadMore = rawLoadMore
          .filter((m: any) => m?.candidateId != null && !findCandLoadMore(m))
          .map((m: any) => String(m.candidateId));
        
        const parsedMatches = rawLoadMore
            .map((m: any) => {
                const cand = findCandLoadMore(m);
                return {
                    candidate: cand,
                    matchScore: m.score ?? 50,
                    reasoning: m.explanation,
                    pros: m.strengths || [],
                    cons: m.risks || []
                };
            })
            .filter((match: any) => match.candidate != null);

        console.log('[VacancyCandidates] Froukje load-more mapping', {
          sentToAI: candidatesToScreen.length,
          aiReturnedRows: rawLoadMore.length,
          mappedWithCandidate: parsedMatches.length,
          unmatchedCandidateIdsSample: unmatchedLoadMore.slice(0, 12),
        });
            
        // Combine with existing matches and re-sort
        const combined = [...matches, ...parsedMatches].sort((a: any, b: any) => b.matchScore - a.matchScore);
        
        setMatches(combined);
        setAllScreeningResults(combined);
        await saveMatchesToDatabase(parsedMatches, vacancyData);
        updateScreeningJob(vacancyId, { isScreening: false });
    } catch(e: any) {
        console.error(e);
        if (e?.message === '429_TOO_MANY_REQUESTS' || e?.message?.includes('429') || e?.message?.includes('Resource exhausted') || e?.message?.includes('Too Many Requests')) {
             alert("Oeps! Froukje heeft iets te veel koffie gedronken en Google vindt dat we te snel gaan. 🤖☕\n\nIk pauzeer even en ga over 30 seconden automatisch verder!");
             setIsScreening(true);
             setTimeout(() => {
                 handleLoadMoreFroukje();
             }, 30000);
             return;
        } else {
             alert("Fout tijdens bijladen van extra CV analyses.");
        }
    } finally {
        setIsScreening(false);
    }
  };

  const handleManualAddCandidate = async (candidate: any) => {
    if (!vacancyData) return;
    setIsScreening(true);
    setInteractivePhase(5); // Start at Froukje phase directly
    setScreeningProgress({ current: 0, total: 1, tokensUsed: 0 });

    try {
      const { screenTalentPoolBatch } = await import('../services/geminiService');
      
      // Ensure candidate has required structure
      const candidateToScreen = { 
        ...candidate, 
        candidate_id: candidate.id,
        parsed_cv: candidate.ai_parsed_data || candidate.parsed_cv || {} 
      };
      
      const enhancedDescription = vacancyData.description + (matchExtraRequest ? `\n\nEXTRA EISEN VAN RECRUITER (STRICT BEOORDELEN HIEROP): ${matchExtraRequest}` : '');
      const newMatches = await screenTalentPoolBatch(
          { title: vacancyData.title, description: enhancedDescription },
          [candidateToScreen],
          1,
          (current, total, tokensUsed) => setScreeningProgress({ current, total, tokensUsed })
      );
      
      const parsedMatches = (newMatches || [])
          .map((m: any) => {
              return {
                  candidate: candidateToScreen, // Use Froukje's mapped profile
                  matchScore: m.score ?? 50,
                  reasoning: m.explanation,
                  pros: m.strengths || [],
                  cons: m.risks || [],
                  isManualAdd: true
              };
          })
          .filter((match: any) => match.candidate != null);
          
      if (parsedMatches.length > 0) {
          // Check if candidate is already in matches
          const existingIndex = matches.findIndex(m => (m.candidate?.id || m.candidate?.candidate_id) === candidateToScreen.id);
          let combined = [];
          if (existingIndex >= 0) {
              combined = [...matches];
              combined[existingIndex] = { ...combined[existingIndex], ...parsedMatches[0], isManualAdd: true };
          } else {
              combined = [parsedMatches[0], ...matches];
          }
          
          combined.sort((a: any, b: any) => b.matchScore - a.matchScore);
          
          setMatches(combined);
          setAllScreeningResults(combined);
          await saveMatchesToDatabase(combined, vacancyData); // Save the entire combined list
      }
    } catch(e: any) {
        console.error(e);
        if (e?.message === '429_TOO_MANY_REQUESTS' || e?.message?.includes('429') || e?.message?.includes('Resource exhausted') || e?.message?.includes('Too Many Requests')) {
             alert("Oeps! Froukje heeft iets te veel koffie gedronken en Google vindt dat we te snel gaan. 🤖☕\n\nIk pauzeer even en ga over 30 seconden automatisch verder!");
             setIsScreening(true);
             setTimeout(() => {
                 handleManualAddCandidate(candidate);
             }, 30000);
             return;
        } else {
             alert("Fout tijdens handmatig toevoegen en analyseren van kandidaat.");
        }
    } finally {
        setIsScreening(false);
        updateScreeningJob(vacancyId, { isScreening: false });
    }
  };

  const promptInviteCandidate = (match: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setConfirmInviteMatch(match);
  };

  const handleInviteCandidate = async (match: any) => {
    if (!vacancyData) return;
    setIsInviting(true);
    try {
      const payload = {
          vacancy_id: vacancyId,
          candidate_id: match.candidate.candidate_id || match.candidate.id,
          status: 'Shortlist',
          match_score: match.matchScore,
          cv_summary: `AI Match (${match.matchScore}%): ${match.reasoning}`
      };

      const { error } = await supabase.from('applications').insert(payload);
      
      if (error) {
          if (error.code === '23505') {
              alert("Deze kandidaat zit al in deze vacature (Dubbel).");
              return;
          }
          throw error;
      }
      
      // Optimistic UI update for instant visual feedback
      setApplications(prev => [...prev, payload]);

      const { data: { user } } = await supabase.auth.getUser();

      // Log to candidate_communications
      try {
        if (user && match.candidate) {
           let validProfileId = match.candidate.candidate_id || match.candidate.id;
           // If match.candidate came from global pool, id might actually be the candidate_id instead of profile_id
           const { data: testProf } = await supabase.from('candidates').select('id').eq('id', validProfileId).maybeSingle();
           if (!testProf) {
               const { data: findProf } = await supabase.from('candidates')
                 .select('id').eq('candidate_id', validProfileId).maybeSingle();
               if (findProf) validProfileId = findProf.id;
               else validProfileId = null;
           }
           
           if (validProfileId) {
               await supabase.from('candidate_communications').insert({
                   profile_id: validProfileId,
                   user_id: user.id,
                   type: 'action',
                   content: `Kandidaat toegevoegd aan vacature pipeline (${vacancyData.title || 'Onbekende vacature'}) vanuit de Talentpool.`
               });
           }
        }
      } catch (logErr) {
        console.warn("Failed to log interaction", logErr);
      }

      // Auto-create a task for the recruiter
      try {
        if (user) {
          await supabase.from('todos').insert({
            title: `Nieuwe kandidaat op vacature: check ${match.candidate.candidate?.name || match.candidate.name || 'Kandidaat'}`,
            description: `Kandidaat is vanuit Talent Pool Screening (AI Match) toegevoegd aan de vacature. Match score: ${match.matchScore}%.`,
            status: 'Pending',
            user_id: user.id,
            vacancy_id: vacancyId,
            candidate_id: match.candidate.candidate_id || match.candidate.id,
            due_date: new Date(Date.now() + 86400000).toISOString() // Due tomorrow
          });
        }
      } catch (todoErr) {
        console.warn("Failed to log todo task", todoErr);
      }

      // Refresh apps and pool candidates
      loadData();
      // Close full screen if open
      setSelectedCandidate(null);
      setConfirmInviteMatch(null);
    } catch (e) {
      console.error(e);
      alert("Kon kandidaat niet toevoegen aan de pipeline. Controleer of de kandidaat al bestaat.");
    } finally {
      setIsInviting(false);
    }
  };

  const handleRateCandidate = async (match: any, rating: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
        // Optimistic UI update
        const newMatches = matches.map(m => {
            if (m.candidate?.id === match.candidate?.id) {
                return {
                    ...m,
                    candidate: {
                        ...m.candidate,
                        settings: { ...(m.candidate.settings || {}), talentpool_rating: rating }
                    }
                };
            }
            return m;
        });
        setMatches(newMatches);

        const profileId = match.candidate?.id;
        if (profileId) {
            const { data: dbProf } = await supabase.from('candidates').select('settings').eq('id', profileId).single();
            const updatedSettings = { ...(dbProf?.settings || {}), talentpool_rating: rating };
            await supabase.from('candidates').update({ settings: updatedSettings }).eq('id', profileId);
        }
    } catch (err) {
        console.error("Error rating candidate:", err);
    }
  };


  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-red-600 rounded-full animate-spin mb-4"></div>
        <p className="text-xs font-black uppercase tracking-wider">Laden...</p>
      </div>
    );
  }

  // --- MODAL VIEW ---
  if (selectedCandidate) {
    // Extract ID safely for standard CandidateDetailModal
    const candidateId = selectedCandidate.candidate?.candidate_id || selectedCandidate.candidate?.id;
    return (
      <div className="absolute inset-0 z-50 bg-white">
        <CandidateDetailModal
          candidateId={candidateId}
          vacancyId={vacancyId}
          clientId={clientId || vacancyData?.client_id || ''}
          onClose={() => setSelectedCandidate(null)}
          onUpdate={() => loadData()}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">

          {/* Simplified Controls */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <h3 className="text-[11px] font-black text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Sliders size={13} className="text-[#e51b23]" /> AI Matching Instellingen
            </h3>

            <div className="space-y-5">
              {/* Min Match Slider */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider">Minimale Match Score</label>
                  <span className={`text-sm font-black px-3 py-0.5 rounded-full border ${minMatchScore >= 80 ? 'bg-green-50 text-green-600 border-green-200' : minMatchScore >= 60 ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>{minMatchScore}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={95}
                  step={5}
                  value={minMatchScore}
                  onChange={e => setMinMatchScore(Number(e.target.value))}
                  className="w-full accent-[#e51b23] h-1.5 rounded-full cursor-pointer"
                />
                <div className="flex justify-between text-xs font-bold text-slate-300 mt-1 uppercase tracking-wider">
                  <span>0%</span><span>Alles tonen</span><span>95%</span>
                </div>
              </div>

              {/* Location + Status filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1.5">Filter op Locatie</label>
                  <input
                    type="text"
                    placeholder="Vul een stad in..."
                    value={matchLocationFilter}
                    onChange={e => setMatchLocationFilter(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] font-bold outline-none focus:border-[#e51b23] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1.5" title="Berekend vanaf standplaats vacature">Max. Afstand</label>
                  <select value={matchDistanceFilter} onChange={e => setMatchDistanceFilter(e.target.value)} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] font-bold outline-none appearance-none focus:border-[#e51b23] transition-colors">
                    <option value="">Alle afstanden</option>
                    <option value="10">Binnen 10 km</option>
                    <option value="25">Binnen 25 km</option>
                    <option value="50">Binnen 50 km</option>
                    <option value="100">Binnen 100 km</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1.5">Filter op Status</label>
                  <select value={matchStatusFilter} onChange={e => setMatchStatusFilter(e.target.value)} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] font-bold outline-none focus:border-[#e51b23] transition-colors appearance-none">
                    <option value="">Alle statussen</option>
                    <option value="Lead">Lead</option>
                    <option value="In gesprek">In gesprek</option>
                    <option value="Shortlist">Shortlist</option>
                    <option value="Beschikbaar">Beschikbaar</option>
                    <option value="Geplaatst">Geplaatst</option>
                    <option value="Afgewezen">Afgewezen</option>
                    <option value="Niet beschikbaar">Niet beschikbaar</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1.5">Beschikbaar per</label>
                  <div className="flex items-center gap-2">
                      <input 
                          type="date" 
                          value={matchAvailabilityFilter}
                          onChange={e => setMatchAvailabilityFilter(e.target.value)}
                          className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] font-bold outline-none focus:border-[#e51b23] transition-colors"
                      />
                      {matchAvailabilityFilter && (
                          <button onClick={() => setMatchAvailabilityFilter('')} className="text-slate-400 hover:text-slate-600 shrink-0">
                              <X size={12} />
                          </button>
                      )}
                  </div>
                </div>
              </div>
              
              <div>
                  <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1.5">Extra Eigen Verzoek (AI Instructie)</label>
                  <textarea
                    placeholder="Bijv: De kandidaat MOET ervaring hebben met Excel en in het bezit zijn van Rijbewijs B."
                    value={matchExtraRequest}
                    onChange={e => setMatchExtraRequest(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[11px] font-bold outline-none focus:border-[#e51b23] transition-colors resize-none h-16"
                  />
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                onClick={handleScreenPool}
                disabled={isScreening}
                className="w-full md:w-auto px-6 py-2.5 bg-[#e51b23] text-white rounded-lg font-black text-xs uppercase tracking-wider hover:bg-[#B71C1C] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md flex items-center justify-center gap-2 active:scale-95"
              >
                {isScreening ? (
                  <><div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Analyseren...</>
                ) : (
                  <><Sparkles size={14} /> Start AI Screening</>
                )}
              </button>
            </div>
          </div>

          {/* Manual Candidate Search for Matching */}
          <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
            <h3 className="text-[11px] font-black text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Search size={13} className="text-indigo-500" /> Handmatig Kandidaat Toevoegen aan AI Match
            </h3>
            <div className="relative">
              <input
                type="text"
                placeholder="Zoek in de talentpool op naam om Froukje direct te laten analyseren..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  runBrowseSearch(e.target.value);
                }}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-[11px] font-bold outline-none focus:border-indigo-500 transition-colors"
              />
              {searchQuery && browseResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto z-50 divide-y divide-slate-100">
                  {browseResults.map(cand => {
                    const name = cand.name || cand.candidate?.name || 'Kandidaat';
                    const role = cand.role || cand.candidate?.role || cand.ai_parsed_data?.currentRole || cand.candidate?.ai_parsed_data?.currentRole || 'Geen functie';
                    const city = cand.address_city || cand.candidate?.address_city || cand.candidate?.city || '';
                    const number = cand.candidate_number || cand.candidate?.candidate_number || '';
                    return (
                    <div 
                      key={cand.id} 
                      onClick={() => {
                        setSearchQuery('');
                        handleManualAddCandidate(cand);
                      }}
                      className="p-3 hover:bg-indigo-50 transition-colors cursor-pointer flex justify-between items-center group"
                    >
                      <div>
                        <div className="text-xs font-bold text-slate-900 group-hover:text-indigo-700">
                          {number ? <span className="text-slate-400 mr-1.5">#{number}</span> : null}
                          {name}
                        </div>
                        <div className="text-[10px] text-slate-500 line-clamp-1">
                          {role} {city ? ` • ${city}` : ''}
                        </div>
                      </div>
                      <button className="px-2 py-1 bg-white border border-slate-200 rounded text-[10px] font-bold text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors flex items-center gap-1 shadow-sm">
                        <Sparkles size={10} /> Toevoegen
                      </button>
                    </div>
                  )})}
                </div>
              )}
              {searchQuery && browseResults.length === 0 && !isBrowseLoading && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg p-4 z-50 text-xs text-slate-500 text-center">
                  Geen kandidaten gevonden voor "{searchQuery}"
                </div>
              )}
              {searchQuery && isBrowseLoading && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg p-4 z-50 flex items-center justify-center">
                   <div className="w-5 h-5 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          {isScreening && (
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm animate-in fade-in zoom-in-95">
              <div className="flex flex-col gap-3 mb-3">
                 {interactivePhase >= 1 && (
                    <div className={`flex items-center gap-2 text-xs font-semibold ${interactivePhase === 1 ? 'text-slate-700 animate-pulse' : 'text-slate-400'}`}>
                        {interactivePhase === 1 ? <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" /> : <CheckCircle2 size={14} className="text-emerald-500" />}
                        Talentpool van {vectorSearchCount ? vectorSearchCount + '+' : 'duizenden'} kandidaten doorzoeken...
                    </div>
                 )}
                 {interactivePhase >= 2 && (
                    <div className={`flex items-center gap-2 text-xs font-semibold ${interactivePhase === 2 ? 'text-slate-700 animate-pulse' : 'text-slate-400'}`}>
                        {interactivePhase === 2 ? <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" /> : <CheckCircle2 size={14} className="text-emerald-500" />}
                        Ophalen van cv's, werkervaring en genoten opleidingen...
                    </div>
                 )}
                 {interactivePhase >= 3 && (
                    <div className={`flex items-center gap-2 text-xs font-semibold ${interactivePhase === 3 ? 'text-slate-700 animate-pulse' : 'text-slate-400'}`}>
                        {interactivePhase === 3 ? <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" /> : <CheckCircle2 size={14} className="text-emerald-500" />}
                        Top 100 kandidaten selecteren...
                    </div>
                 )}
                 {interactivePhase >= 4 && (
                    <div className={`flex items-center gap-2 text-xs font-semibold ${interactivePhase === 4 ? 'text-slate-700 animate-pulse' : 'text-slate-400'}`}>
                        {interactivePhase === 4 ? <div className="w-3.5 h-3.5 rounded-full border-2 border-slate-300 border-t-slate-700 animate-spin" /> : <CheckCircle2 size={14} className="text-emerald-500" />}
                        Snelste {screenLimit} profielen voorbereiden voor Diepgaande AI Evaluatie...
                    </div>
                 )}
                 
                 {interactivePhase >= 5 && (
                    <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-100 mt-1">
                        <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                        <BrainCircuit size={15} className="text-[#e51b23] animate-pulse" />
                        Froukje onderzoekt profiel {displayProgress > 0 ? displayProgress : 1} van {screeningProgress.total > 0 ? screeningProgress.total : screenLimit}...
                        </h4>
                        <span className="text-xs font-bold text-slate-400 bg-white px-3 py-1 rounded-full border border-slate-200">
                        {displayProgress} / {screeningProgress.total || screenLimit}
                        </span>
                    </div>
                 )}
              </div>
              
              {interactivePhase >= 5 && (
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden mt-1">
                    <div
                      className="bg-[#e51b23] h-2 rounded-full transition-all duration-500 delay-75"
                      style={{ width: `${Math.max(4, (displayProgress / (screeningProgress.total || screenLimit)) * 100)}%` }}
                    />
                  </div>
              )}
            </div>
          )}

          {/* No Results */}
          {screeningComplete && matches.length === 0 && (
            <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
              <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-300"><User size={24} /></div>
              <h3 className="text-sm font-black text-slate-700 mb-1">Geen matches gevonden</h3>
              <p className="text-xs text-slate-400">Verlaag de minimale score of pas filters aan.</p>
            </div>
          )}

          {/* Results Table */}
          {matches.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-wider">
                  Aanbevolen Kandidaten ({filteredMatches.filter(m => m.matchScore >= minMatchScore).length}{matches.length !== filteredMatches.length ? ` / ${matches.length}` : ''})
                </h3>
              </div>

              {/* Bulk Actions */}
              {selectedMatches.length > 0 && (
                <div className="bg-red-50 border border-red-200 p-3 rounded-xl flex items-center justify-between animate-fadeIn">
                  <span className="text-[11px] font-black text-[#e51b23]">{selectedMatches.length} geselecteerd</span>
                  <div className="flex gap-2">
                    <button onClick={handleBulkEmail} className="px-3 py-1.5 bg-white text-[#e51b23] border border-red-200 hover:bg-red-50 rounded-lg text-[10px] font-bold flex items-center gap-1.5">
                      <Mail size={13} /> E-mail
                    </button>
                    <button onClick={handleBulkWhatsapp} className="px-3 py-1.5 bg-[#25D366] text-white rounded-lg text-[10px] font-bold flex items-center gap-1.5">
                      <MessageCircle size={13} /> WhatsApp
                    </button>
                  </div>
                </div>
              )}

              <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-700 min-w-[700px]">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <th className="p-3 w-10 cursor-pointer" onClick={(e) => handleSelectAll(e)}>
                        <div className="text-slate-400">{selectedMatches.length === filteredMatches.length && filteredMatches.length > 0 ? <CheckSquare size={16} className="text-[#e51b23]" /> : <Square size={16} />}</div>
                      </th>
                      <th className="p-3 font-black text-[10px] uppercase tracking-wider text-slate-400 w-20 cursor-pointer hover:text-slate-600 transition-colors" onClick={() => setMatchSortOrder('score')}>
                        Score {matchSortOrder === 'score' && <ChevronDown size={10} className="inline ml-0.5" />}
                      </th>
                      <th className="p-3 font-black text-[10px] uppercase tracking-wider text-slate-400 w-48 max-w-[200px]">Kandidaat</th>
                      <th className="p-3 font-black text-[10px] uppercase tracking-wider text-slate-400 cursor-pointer hover:text-slate-600 transition-colors" onClick={() => setMatchSortOrder('distance')}>
                        Woonplaats {matchSortOrder === 'distance' && <ChevronDown size={10} className="inline ml-0.5" />}
                      </th>
                      <th className="p-3 font-black text-[10px] uppercase tracking-wider text-slate-400">Status</th>
                      <th className="p-3 font-black text-[10px] uppercase tracking-wider text-slate-400">Beschikbaarheid</th>
                      <th className="p-3 font-black text-[10px] uppercase tracking-wider text-slate-400">AI Samenvatting</th>
                      <th className="p-3 font-black text-[10px] uppercase tracking-wider text-slate-400 text-right">Actie</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMatches.filter(m => m.matchScore >= minMatchScore).slice(0, displayCount).map((match, idx) => {
                      const profileId = match.candidate?.id || match.candidate?.candidate?.id;
                      const isSelected = selectedMatches.includes(profileId);
                      const city = match.candidate?.candidate?.address_city || match.candidate?.address_city || match.candidate?.candidate?.city || '-';
                      const distance = city !== '-' && cityDistances[city.toLowerCase()] !== undefined && cityDistances[city.toLowerCase()] >= 0 
                          ? `${cityDistances[city.toLowerCase()]} km` : '';
                      return (
                        <React.Fragment key={idx}>
                          <tr
                            onClick={() => setExpandedRow(expandedRow === profileId ? null : profileId)}
                            className={`border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer group ${isSelected ? 'bg-red-50/20' : ''}`}
                          >
                            <td className="p-3 w-10" onClick={(e) => handleSelectMatch(profileId, e)}>
                              <div>{isSelected ? <CheckSquare size={16} className="text-[#e51b23]" /> : <Square size={16} className="text-slate-200 group-hover:text-slate-300" />}</div>
                            </td>
                            <td className="p-3 align-top w-20">
                              <span className={`text-base font-black ${match.matchScore >= 80 ? 'text-green-600' : match.matchScore >= 60 ? 'text-amber-500' : 'text-slate-400'}`}>
                                {match.matchScore}%
                              </span>
                            </td>
                            <td className="p-3 align-top w-48 max-w-[200px]" onClick={(e) => { e.stopPropagation(); setSelectedCandidate(match); }}>
                              <div className="font-bold text-slate-900 hover:text-[#e51b23] transition-colors text-xs break-words leading-tight mb-1 flex flex-wrap items-center gap-1.5">
                                <span>{match.candidate?.candidate?.name || match.candidate?.name || 'Onbekend'}</span>
                                {match.isManualAdd && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-black bg-purple-100 text-purple-700 uppercase tracking-widest" title="Handmatig toegevoegd">
                                    Handmatig
                                  </span>
                                )}
                              </div>
                              <div className="text-[11px] text-slate-500 font-medium whitespace-normal leading-relaxed">
                                {match.candidate?.candidate?.role || match.candidate?.role || match.candidate?.ai_parsed_data?.currentRole || match.candidate?.parsed_cv?.currentRole || 'Kandidaat'}
                              </div>
                            </td>
                            <td className="p-3 align-top">
                              <span className="text-[11px] font-medium text-slate-600">{city}</span>
                              <span className="block text-[10px] text-slate-400 mt-0.5" title="Afstand vanaf de vacature locatie">📍 {distance || '(Binnenkort)'}</span>
                            </td>
                            <td className="p-3 align-top">
                              {match.candidate?.status ? (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-black uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200 whitespace-nowrap">
                                  {match.candidate.status}
                                </span>
                              ) : <span className="text-slate-300">-</span>}
                            </td>
                            <td className="p-3 align-top">
                              {(() => {
                                const avail = match.candidate?.ai_parsed_data?.available_from || match.candidate?.candidate?.available_from || match.candidate?.parsed_cv?.available_from || match.candidate?.available_from;
                                if (!avail) return <span className="text-slate-300 text-[10px]">-</span>;
                                const dt = new Date(avail);
                                if (isNaN(dt.getTime())) return <span className="text-slate-300 text-[10px]">-</span>;
                                const isPast = dt <= new Date();
                                return (
                                  <span className={`text-[10px] font-bold ${isPast ? 'text-emerald-600' : 'text-slate-500'}`}>
                                    {dt.toLocaleDateString('nl-NL')}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="p-3 align-top w-1/3 max-w-sm">
                              {match.reasoning && (
                                <p className="text-[11px] leading-relaxed text-slate-600 font-medium mb-3 line-clamp-2">
                                  {match.reasoning}
                                </p>
                              )}
                              
                              <div className="flex flex-col gap-1.5 mb-2.5">
                                {match.pros && match.pros.length > 0 && match.pros.slice(0, 3).map((pro: string, i: number) => (
                                  <div key={`pro-${i}`} className="flex items-start gap-1.5">
                                    <CheckCircle2 size={12} className="text-emerald-500 shrink-0 mt-[1px]" />
                                    <span className="text-[11px] text-slate-600 truncate" title={pro}>{pro}</span>
                                  </div>
                                ))}
                                {match.cons && match.cons.length > 0 && match.cons.slice(0, 3).map((con: string, i: number) => (
                                  <div key={`con-${i}`} className="flex items-start gap-1.5">
                                    <XCircle size={12} className="text-rose-500 shrink-0 mt-[1px]" />
                                    <span className="text-[11px] text-slate-600 truncate" title={con}>{con}</span>
                                  </div>
                                ))}
                              </div>

                              <button onClick={(e) => { e.stopPropagation(); setExpandedRow(expandedRow === profileId ? null : profileId); }} className="text-xs font-bold text-[#e51b23] hover:underline flex items-center gap-1 inline-flex mt-1">
                                Details {expandedRow === profileId ? 'verbergen' : 'bekijken'} <ChevronDown size={10} className={`transform transition-transform ${expandedRow === profileId ? 'rotate-180' : ''}`} />
                              </button>
                            </td>
                            <td className="p-3 align-top text-right">
                              {applications.some((app: any) => app.candidate_id === profileId) ? (
                                <span className="px-3 py-1.5 bg-slate-100 text-slate-400 text-xs font-black uppercase tracking-wider rounded-lg border border-slate-200 inline-flex items-center gap-1.5 cursor-not-allowed">
                                  <CheckSquare size={13} /> Toegevoegd
                                </span>
                              ) : (
                                <button
                                  onClick={(e) => promptInviteCandidate(match, e)}
                                  className="px-3 py-1.5 bg-slate-900 hover:bg-black text-white text-xs font-black uppercase tracking-wider rounded-lg transition-colors inline-flex items-center gap-1.5"
                                >
                                  <Plus size={13} /> Toevoegen
                                </button>
                              )}
                            </td>
                          </tr>
                          {expandedRow === profileId && (
                            <tr className="bg-slate-50/50 border-b border-slate-100">
                                <td colSpan={7} className="p-4 px-12">
                                    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
                                        <div>
                                            <h4 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-2 mt-0">Uitleg van AI</h4>
                                            <p className="text-xs text-slate-700 leading-relaxed italic m-0">"{match.reasoning}"</p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <h4 className="text-xs font-black uppercase tracking-wider text-emerald-600 mb-2 flex items-center gap-1.5 mt-0"><CheckCircle2 size={12}/> Pluspunten</h4>
                                                <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside m-0">
                                                    {match.pros && match.pros.length > 0 ? match.pros.map((pro: string, i: number) => <li key={i}>{pro}</li>) : <li className="text-slate-400 italic">Geen specifieke pluspunten</li>}
                                                </ul>
                                            </div>
                                            <div>
                                                <h4 className="text-xs font-black uppercase tracking-wider text-rose-600 mb-2 flex items-center gap-1.5 mt-0"><XCircle size={12}/> Minpunten</h4>
                                                <ul className="text-xs text-slate-600 space-y-1 list-disc list-inside m-0">
                                                    {match.cons && match.cons.length > 0 ? match.cons.map((con: string, i: number) => <li key={i}>{con}</li>) : <li className="text-slate-400 italic">Geen specifieke minpunten</li>}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Visuele Paginatie & Extra Zoeken */}
              <div className="flex justify-between items-center pt-2 pb-4">
                  {sortedMatches.filter(m => m.matchScore >= minMatchScore).length > displayCount ? (
                    <button
                        onClick={() => setDisplayCount(prev => prev + 25)}
                        className="px-5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-bold transition-colors"
                    >
                        + Toon {Math.min(25, sortedMatches.filter(m => m.matchScore >= minMatchScore).length - displayCount)} meer kandidaten
                    </button>
                  ) : <div/>}

                  <button
                    onClick={() => {
                        // Just run load more which uses existing vector matches!
                        setTimeout(handleLoadMoreFroukje, 0);
                    }}
                    disabled={isScreening}
                    className="px-5 py-2 bg-[#e51b23] hover:bg-[#B71C1C] text-white rounded-lg text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all shadow-md active:scale-95 disabled:opacity-50"
                  >
                     {isScreening ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Sparkles size={14} />} 
                     Zoek meer kandidaten
                  </button>
              </div>
              {/* Load more */}
              {allScreeningResults.length > matches.length && (
                <div className="flex justify-center pt-1 pb-2">
                  <button
                    onClick={() => {
                      const nextBatch = allScreeningResults.slice(matches.length, matches.length + 50);
                      setMatches(prev => [...prev, ...nextBatch]);
                    }}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 font-black text-xs uppercase tracking-wider transition-all shadow-sm active:scale-95"
                  >
                    <ChevronDown size={14} /> Toon nog 50 ({allScreeningResults.length - matches.length} meer)
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

      {showEmailModal && (
        <BulkEmailComposerModal 
           isOpen={showEmailModal} 
           onClose={() => setShowEmailModal(false)} 
           selectedCandidates={matches.filter(m => {
               const id = m.candidate?.id || m.candidate?.candidate?.id;
               return selectedMatches.includes(id);
           }).map(m => ({
               id: m.candidate?.id || m.candidate?.candidate?.id,
               name: m.candidate?.candidate?.name || m.candidate?.name || 'Onbekend',
               email: m.candidate?.candidate?.email || m.candidate?.email || ''
           }))}
           vacancyData={vacancyData} 
        />
      )}

      {confirmInviteMatch && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in">
           <div className="bg-white rounded-2xl p-6 shadow-xl w-full max-w-sm border border-slate-100 animate-in zoom-in-95">
             <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                <Plus size={24} />
             </div>
             <h3 className="text-lg font-black text-slate-900 text-center mb-2">Kandidaat Toevoegen</h3>
             <p className="text-sm text-slate-500 text-center mb-6">
                Weet je zeker dat je <strong>{confirmInviteMatch.candidate?.candidate?.name || confirmInviteMatch.candidate?.name}</strong> wilt toevoegen aan de vacature?
             </p>
             <div className="flex items-center gap-3">
               <button 
                  onClick={() => setConfirmInviteMatch(null)}
                  disabled={isInviting}
                  className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold text-xs transition-colors disabled:opacity-50"
               >
                  Annuleren
               </button>
               <button 
                  onClick={() => handleInviteCandidate(confirmInviteMatch)}
                  disabled={isInviting}
                  className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-xs transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
               >
                  {isInviting ? <div className="w-4 h-4 border-2 border-slate-300 border-t-white rounded-full animate-spin" /> : 'Toevoegen'}
               </button>
             </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default VacancyCandidates;
